import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, MoreVertical, Printer, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { printAllPoliciesAsDocument, printSinglePolicyAsDocument } from "./policySummaryPrint";
import {
  buildConfirmationPolicyDocumentHtml,
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
  printConfirmationPolicyDocumentHtml,
} from "./confirmationPolicyDocument";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";
import PolicyFormWizardShell from "./policy-wizard/PolicyFormWizardShell";
import PolicyWorkflowStepSection from "./policy-wizard/PolicyWorkflowStepSection";
import PolicyChecklistStepSection from "./policy-wizard/PolicyChecklistStepSection";
import PolicyLettersStepSection, {
  PolicyLettersField,
  PolicyLettersSelect,
  PolicyLettersYesNo,
} from "./policy-wizard/PolicyLettersStepSection";
import usePolicyCompanyRoles from "./policy-wizard/usePolicyCompanyRoles";
import { CONFIRMATION_POLICY_STEPS } from "./policy-wizard/policyWizardSteps";
import {
  validateBasicsStep,
  validateWorkflowStep,
  validateLettersStep,
  validateChecklistStep,
  validateApplicabilityStep,
  createPolicyWizardNav,
  ORG_APPLICABILITY_KEYS,
} from "./policy-wizard/policyWizardValidation";
import { confirmationPolicyService } from "../../services/api";
import PolicyDeleteImpactModal from "./policy-wizard/PolicyDeleteImpactModal";
import PolicyAppliedMastersEye from "./policy-wizard/PolicyAppliedMastersEye";
import PolicyListTableHeader, {
  policyListRowActionClass,
  policyListStatusClass,
} from "./policy-wizard/PolicyListTableHeader";
import PolicyListToolbar from "./policy-wizard/PolicyListToolbar";
import { DsButton } from "../../design-system";
import { confirmPolicyDelete, startPolicyDeleteFlow } from "../../utils/policyDeleteFlow";
import PolicyApplicabilityStepSection from "./policy-wizard/PolicyApplicabilityStepSection";

const CONFIRMATION_WORKFLOW_TABS = [
  { id: "approval", label: "Confirmation", key: "workflowSteps", chainLabel: "confirmation request" },
  { id: "extension", label: "Extension", key: "extensionWorkflowSteps", chainLabel: "extension request" },
  { id: "termination", label: "Termination", key: "terminationWorkflowSteps", chainLabel: "termination request" },
];
const CONFIRMATION_APPROVAL_METHODS = [
  { value: "auto_approved", label: "Auto approved", hint: "No approval chain" },
  { value: "workflow", label: "Through workflow", hint: "Multi-step reviewers" },
  { value: "on_instance", label: "On instance", hint: "HR assigns per case" },
];

const ConfirmationPolicyComponent = ({
  darkMode,
  branchesList,
  departmentsList,
  designationsList,
  subDepartmentsList,
  gradesList,
  categoriesList,
  companyName = MASTERS_DOCUMENT_COMPANY_NAME,
  companyAddress = MASTERS_DOCUMENT_COMPANY_ADDRESS,
  policyPreparedBy = "Human Resources",
  policyApprovedBy = "Management",
  policies = [],
  onPolicyChange,
  loadingPolicies = false,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [showInactivePolicies, setShowInactivePolicies] = useState(false);
  const [policySearch, setPolicySearch] = useState("");
  const [workflowStepsTab, setWorkflowStepsTab] = useState("approval");
  const [policyFormStep, setPolicyFormStep] = useState(1);
  const [policyHelpOpen, setPolicyHelpOpen] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  /** Blocks save: effective date unchanged after edits, or applicability overlap — use modals, not only inline text. */
  const [saveGateModal, setSaveGateModal] = useState(null);
  const [policySaveError, setPolicySaveError] = useState(null);
  const [approvalSettingsOpen, setApprovalSettingsOpen] = useState(false);
  const [approvalRolesForPolicy, setApprovalRolesForPolicy] = useState(["management", "ceo"]);
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [flowchartPolicy, setFlowchartPolicy] = useState(null);
  const [policyDocModal, setPolicyDocModal] = useState(null);
  const [policyHistoryModalId, setPolicyHistoryModalId] = useState(null);
  const [historyPreviewEntry, setHistoryPreviewEntry] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [deletePolicyModal, setDeletePolicyModal] = useState(null);
  const [deletePolicyLoading, setDeletePolicyLoading] = useState(false);
  const policyHelpRef = useRef(null);
  const approvalSettingsRef = useRef(null);
  const { policyRoleOptions, defaultPolicyRoleValue } = usePolicyCompanyRoles();

  const docCompanyName =
    (typeof companyName === "string" && companyName.trim()) || MASTERS_DOCUMENT_COMPANY_NAME;
  const docCompanyAddress =
    (typeof companyAddress === "string" && companyAddress.trim()) || MASTERS_DOCUMENT_COMPANY_ADDRESS;

  const WORKFLOW_ROLES = [
    { value: "manager", label: "Manager" },
    { value: "hr", label: "HR" },
    { value: "management", label: "Management" },
    { value: "reporting_manager", label: "Reporting Manager" },
  ];
  const APPROVAL_ROLES = [
    { value: "ceo", label: "CEO" },
    { value: "management", label: "Management" },
    { value: "hr", label: "HR" },
    { value: "manager", label: "Manager" },
    { value: "reporting_manager", label: "Reporting Manager" },
  ];
  const LETTER_FORMAT_OPTIONS = [
    { value: "", label: "Select letter format" },
    { value: "standard", label: "Standard Confirmation Letter" },
    { value: "detailed", label: "Detailed Confirmation Letter" },
    { value: "custom_1", label: "Custom Template 1" },
    { value: "custom_2", label: "Custom Template 2" },
  ];
  const PROBATION_PERIOD_OPTIONS = [
    { value: "3_months", label: "3 months", days: 90 },
    { value: "6_months", label: "6 months", days: 180 },
    { value: "custom", label: "Custom (days)" },
  ];
  const START_DATE_LOGIC_OPTIONS = [
    { value: "doj_based", label: "DOJ based" },
    { value: "custom", label: "Custom start date" },
  ];
  const REMINDER_DAYS_OPTIONS = [30, 15, 7];
  const EXTENSION_DURATION_OPTIONS = [
    { value: "fixed", label: "Fixed (e.g. 90 days)" },
    { value: "custom", label: "Custom per request" },
  ];
  const FNF_TRIGGER_OPTIONS = [
    { value: "auto", label: "Auto" },
    { value: "manual", label: "Manual" },
  ];
  const CHECKLIST_TRIGGER_EVENTS = [
    { value: "confirmation", label: "Confirmation approval" },
    { value: "extension", label: "Extension approval" },
    { value: "termination", label: "Termination approval" },
  ];
  const CHECKLIST_DEPARTMENTS = [
    { value: "", label: "Select department" },
    { value: "hr", label: "HR" },
    { value: "it", label: "IT" },
    { value: "admin", label: "Admin" },
    { value: "finance", label: "Finance" },
  ];
  const CHECKLIST_ACTION_ROLES = [
    { value: "manager", label: "Manager" },
    { value: "hr", label: "HR" },
    { value: "management", label: "Management" },
    { value: "reporting_manager", label: "Reporting Manager" },
    { value: "it", label: "IT" },
    { value: "admin", label: "Admin" },
    { value: "finance", label: "Finance" },
  ];
  const DUE_DATE_LOGIC_OPTIONS = [
    { value: "approval_date", label: "From approval date" },
    { value: "fixed_days", label: "Fixed days from trigger" },
  ];
  const LOCATION_OPTIONS = [
    { value: "", label: "All Locations" },
    { value: "1", label: "Mumbai" },
    { value: "2", label: "Delhi" },
    { value: "3", label: "Bangalore" },
  ];
  /** Applicability — employment type (multi-select in UI; empty = all types). */
  const EMPLOYMENT_TYPE_OPTIONS = [
    { value: "full_time", label: "Full-time" },
    { value: "contract", label: "Contract" },
    { value: "intern", label: "Intern" },
    { value: "consultant", label: "Consultant" },
    { value: "external", label: "External" },
    { value: "part_time", label: "Part-time" },
    { value: "trainee", label: "Trainee" },
  ];
  const policyDocumentOptions = useMemo(
    () => ({
      companyName: docCompanyName,
      companyAddress: docCompanyAddress,
      preparedBy: policyPreparedBy,
      approvedBy: policyApprovedBy,
      workflowRoleLabel: (role) =>
        WORKFLOW_ROLES.find((r) => r.value === role)?.label || role || "—",
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
      locationOptions: LOCATION_OPTIONS,
      letterFormatOptions: LETTER_FORMAT_OPTIONS,
      probationPeriodOptions: PROBATION_PERIOD_OPTIONS,
      startDateLogicOptions: START_DATE_LOGIC_OPTIONS,
      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS,
    }),
    [
      docCompanyName,
      docCompanyAddress,
      policyPreparedBy,
      policyApprovedBy,
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
    ]
  );

  const createDefaultPolicy = () => ({
    policyName: "",
    effectiveDate: new Date().toISOString().slice(0, 10),
    autoAssignOnJoin: false,
    allEmployees: false,
    probationDays: 90,
    initialProbationPeriod: "3_months",
    customProbationDays: 90,
    startDateLogic: "doj_based",
    minServiceDaysForConfirmation: 90,
    autoConfirmation: "no",
    reminderDays: 7,
    reminderTriggerDays: [30, 15, 7],
    confirmationType: "automatic",
    reminderTriggerDay: 7,
    limitProbationExtensions: 1,
    performanceReviewMandatoryBeforeConfirmation: "no",
    extensionAllowed: "yes",
    maxExtensions: 2,
    extensionDurationType: "fixed",
    extensionDurationDays: 90,
    extensionReasonMandatory: "yes",
    performanceReviewMandatoryBeforeExtension: "no",
    finalExtensionAutoTriggerTermination: "no",
    terminationDuringProbationAllowed: "yes",
    terminationNoticePeriodDays: 0,
    terminationMultiLevelApproval: "yes",
    approvalMethod: "auto_approved",
    fullAndFinalSettlementTrigger: "manual",
    needWorkflowSteps: false,
    workflowParallelApproval: false,
    workflowEscalationAfterDays: 3,
    workflowAutoApprovalIfNoAction: false,
    workflowRejectionWithReason: true,
    workflowReinitiationAllowed: true,
    workflowSteps: [],
    cancellationWorkflowSteps: [],
    extensionWorkflowSteps: [],
    terminationWorkflowSteps: [],
    checklistMappings: [],
    autoLetter: "no",
    letterFormat: "",
    letterConfirmation: "",
    letterExtension: "",
    letterTermination: "",
    experienceLetterAllowed: "yes",
    branch: "",
    department: "",
    designation: "",
    grade: "",
    category: "",
    subDepartment: "",
    location: "",
    individualEmployeeIds: "",
    employmentTypes: [],
    alerts: {
      hr: true,
      manager: true,
      employee: true,
    },
    policyDocumentPushed: false,
    policyPushedAt: null,
    policyVersion: 1,
    policyVersionHistory: [],
    policySnapshots: [],
  });

  const normalizePolicyRow = (row) => {
    const d = createDefaultPolicy();
    const at = row.assignedTo || {};
    const policyName = row.policyName || row.name || "";
    const pickOrg = (key, assignedKey) => {
      if (row[key] != null && row[key] !== "") return String(row[key]);
      const fromAssigned = at[assignedKey]?.[0];
      return fromAssigned != null ? String(fromAssigned) : "";
    };
    return {
      ...d,
      ...row,
      policyName,
      allEmployees: !!row.allEmployees || !!at.allEmployees,
      branch: pickOrg("branch", "branches"),
      department: pickOrg("department", "departments"),
      designation: pickOrg("designation", "designations"),
      grade: pickOrg("grade", "grades"),
      category: pickOrg("category", "categories"),
      subDepartment: pickOrg("subDepartment", "subDepartments"),
      location: row.location || "",
      employmentTypes:
        Array.isArray(row.employmentTypes) && row.employmentTypes.length
          ? row.employmentTypes
          : Array.isArray(at.employmentTypes)
            ? [...at.employmentTypes]
            : [],
      individualEmployeeIds:
        row.individualEmployeeIds != null && row.individualEmployeeIds !== ""
          ? Array.isArray(row.individualEmployeeIds)
            ? row.individualEmployeeIds.join(", ")
            : String(row.individualEmployeeIds)
          : Array.isArray(at.employees) && at.employees.length
            ? at.employees.join(", ")
            : "",
      workflowSteps: Array.isArray(row.workflowSteps) ? row.workflowSteps : [],
      extensionWorkflowSteps: Array.isArray(row.extensionWorkflowSteps)
        ? row.extensionWorkflowSteps
        : [],
      terminationWorkflowSteps: Array.isArray(row.terminationWorkflowSteps)
        ? row.terminationWorkflowSteps
        : [],
      checklistMappings: Array.isArray(row.checklistMappings) ? row.checklistMappings : [],
      alerts: row.alerts || { hr: true, manager: true, employee: true },
      policyVersionHistory: Array.isArray(row.policyVersionHistory) ? row.policyVersionHistory : [],
      policySnapshots: Array.isArray(row.policySnapshots) ? row.policySnapshots : [],
    };
  };

  const buildAssignedTo = (p) => {
    const ids = String(p.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const employees = ids.map((x) => {
      const n = Number(x);
      return Number.isNaN(n) ? x : n;
    });
    if (p.allEmployees) {
      return {
        allEmployees: true,
        branches: [],
        departments: [],
        designations: [],
        grades: [],
        categories: [],
        subDepartments: [],
        employmentTypes: [],
        employees: [],
      };
    }
    const pickId = (value) => {
      if (!value) return [];
      const n = Number(value);
      return [Number.isNaN(n) ? value : n];
    };
    return {
      allEmployees: false,
      branches: pickId(p.branch),
      departments: pickId(p.department),
      designations: pickId(p.designation),
      grades: pickId(p.grade),
      categories: pickId(p.category),
      subDepartments: pickId(p.subDepartment),
      employmentTypes: Array.isArray(p.employmentTypes) ? [...p.employmentTypes] : [],
      employees,
    };
  };

  const mergePayloadForApi = (form) => {
    const policyName = String(form.policyName || "").trim();
    return {
      ...form,
      name: policyName || form.name || "",
      policyName,
      assignedTo: buildAssignedTo(form),
      approvalRolesForPolicy: Array.isArray(form.approvalRolesForPolicy)
        ? form.approvalRolesForPolicy
        : [],
    };
  };

  const buildConfirmationPolicyApiPayload = (form, rolesForPolicy = []) => {
    const payload = mergePayloadForApi({
      ...form,
      approvalRolesForPolicy: rolesForPolicy,
    });
    const {
      id: _id,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      ...apiPayload
    } = payload;
    return apiPayload;
  };

  const [newPolicy, setNewPolicy] = useState(createDefaultPolicy());
  const normalizeChecklistItem = (item = {}) => ({
    triggerEvent: item.triggerEvent || "confirmation",
    department: item.department || "",
    actionRole: item.actionRole || "hr",
    name: item.name || "",
    mandatory: typeof item.mandatory === "boolean" ? item.mandatory : true,
    dueDateLogic: item.dueDateLogic || "approval_date",
    dueDateDays: typeof item.dueDateDays === "number" ? item.dueDateDays : 7,
  });

  const openCreateForm = () => {
    setIsEditing(false);
    setEditingPolicyId(null);
    setWorkflowStepsTab("approval");
    setPolicyFormStep(1);
    setPolicySaveError(null);
    setPolicyHelpOpen(false);
    setSaveGateModal(null);
    setNewPolicy(createDefaultPolicy());
    setIsFormOpen(true);
  };

  const openEditForm = (policy) => {
    setIsEditing(true);
    setEditingPolicyId(policy.id);
    setWorkflowStepsTab("approval");
    setPolicyFormStep(1);
    setPolicySaveError(null);
    setPolicyHelpOpen(false);
    setSaveGateModal(null);
    const normalized = normalizePolicyRow(policy);
    setNewPolicy({
      ...normalized,
      checklistMappings: (normalized.checklistMappings || []).map(normalizeChecklistItem),
    });
    if (Array.isArray(policy.approvalRolesForPolicy)) {
      setApprovalRolesForPolicy(policy.approvalRolesForPolicy);
    }
    setIsFormOpen(true);
  };

  useEffect(() => {
    onPolicyChange?.();
  }, [onPolicyChange]);

  useEffect(() => {
    if (!policyHelpOpen) return;
    const onMouseDown = (e) => {
      if (policyHelpRef.current && !policyHelpRef.current.contains(e.target)) {
        setPolicyHelpOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [policyHelpOpen]);

  useEffect(() => {
    if (!approvalSettingsOpen) return;
    const onMouseDown = (e) => {
      if (approvalSettingsRef.current && !approvalSettingsRef.current.contains(e.target)) {
        setApprovalSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [approvalSettingsOpen]);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [actionMenu]);

  const requestSavePolicyForm = (opts = {}) => {
    const skipOverlapCheck = !!opts.skipOverlapCheck;
    setPolicySaveError(null);
    setSaveGateModal(null);
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const candidate = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(normalizePolicyRow(saved));
        const sigNew = policyContentSignature({ ...candidate, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (candidate.effectiveDate || "")) {
          setSaveGateModal({ type: "effective_date" });
          return;
        }
      }
    }

    if (!skipOverlapCheck) {
      const conflicts = findConflictingPolicies(
        { ...candidate, id: candidate.id || editingPolicyId || "__new__" },
        policies.map((p) => normalizePolicyRow(p)),
        isEditing ? editingPolicyId : null
      );
      if (conflicts.length > 0) {
        setSaveGateModal({
          type: "overlap",
          items: conflicts.map((p) => ({
            id: p.id,
            name: p.policyName || p.name || "Untitled policy",
            detail: summarizeOverlapReason(candidate, p),
          })),
        });
        return;
      }
    }

    if (!String(newPolicy.policyName || "").trim()) {
      setPolicySaveError("Policy name is required.");
      return;
    }
    if (newPolicy.initialProbationPeriod === "custom" && (!newPolicy.customProbationDays || Number(newPolicy.customProbationDays) <= 0)) {
      setPolicySaveError("Probation period must be greater than 0.");
      return;
    }
    if (newPolicy.extensionAllowed === "yes") {
      if (!newPolicy.maxExtensions || Number(newPolicy.maxExtensions) < 1) {
        setPolicySaveError("Max extension count must be at least 1.");
        return;
      }
      if (newPolicy.extensionDurationType === "fixed" && (!newPolicy.extensionDurationDays || Number(newPolicy.extensionDurationDays) < 1)) {
        setPolicySaveError("Extension duration must be at least 1.");
        return;
      }
    }
    if (
      newPolicy.approvalMethod === "workflow" &&
      (!newPolicy.workflowSteps || newPolicy.workflowSteps.length === 0)
    ) {
      setPolicySaveError("Add at least one workflow step for 'Through workflow'.");
      return;
    }
    setShowSummaryModal(true);
  };

  const continueAfterOverlapAck = () => {
    setSaveGateModal(null);
    requestSavePolicyForm({ skipOverlapCheck: true });
  };

  const handleSavePolicy = async () => {
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const form = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(normalizePolicyRow(saved));
        const sigNew = policyContentSignature({ ...form, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (form.effectiveDate || "")) {
          toast.error(
            "Policy content changed without a new effective date. Update the effective date before saving."
          );
          return;
        }
      }
    }

    const basePayload = buildConfirmationPolicyApiPayload(form, approvalRolesForPolicy);
    const savedAt = new Date().toISOString();
    let merged;

    setSavingPolicy(true);
    try {
      if (isEditing && editingPolicyId) {
        const prev = policies.find((p) => p.id === editingPolicyId);
        if (!prev) {
          toast.error("Policy not found");
          return;
        }
        const v = computeVersionOnSave(normalizePolicyRow(prev), basePayload, false);
        merged = {
          ...normalizePolicyRow(prev),
          ...basePayload,
          policyVersion: v.policyVersion,
          policyVersionHistory: v.policyVersionHistory,
          updatedAt: savedAt,
          policyDocumentPushed: prev.policyDocumentPushed,
          policyPushedAt: prev.policyPushedAt,
        };
        merged.policySnapshots = mergePolicySnapshots(
          normalizePolicyRow(prev),
          merged,
          v,
          false,
          savedAt
        );
        const res = await confirmationPolicyService.updateConfirmationPolicy(editingPolicyId, merged);
        if (res.success) {
          toast.success(
            v.versionBumped
              ? "Policy saved. Document version increased (new effective date is in the future and after the previous effective date)."
              : "Confirmation policy updated"
          );
          onPolicyChange?.(res.data);
          setIsFormOpen(false);
          setIsEditing(false);
          setEditingPolicyId(null);
          setPolicyFormStep(1);
          setShowSummaryModal(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const v = computeVersionOnSave(null, basePayload, true);
        merged = {
          ...basePayload,
          policyVersion: v.policyVersion,
          policyVersionHistory: v.policyVersionHistory,
          status: "active",
          createdAt: savedAt,
          updatedAt: savedAt,
          policyDocumentPushed: false,
          policyPushedAt: null,
        };
        merged.policySnapshots = mergePolicySnapshots(null, merged, v, true, savedAt);
        const res = await confirmationPolicyService.createConfirmationPolicy(merged);
        if (res.success) {
          toast.success("Confirmation policy created");
          onPolicyChange?.(res.data);
          setIsFormOpen(false);
          setIsEditing(false);
          setEditingPolicyId(null);
          setPolicyFormStep(1);
          setShowSummaryModal(false);
        } else {
          toast.error(res.message || "Create failed");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleCancelPolicy = () => {
    setIsFormOpen(false);
    setIsEditing(false);
    setEditingPolicyId(null);
    setWorkflowStepsTab("approval");
    setPolicyFormStep(1);
    setPolicyHelpOpen(false);
    setPolicySaveError(null);
    setSaveGateModal(null);
  };

  const handleDeactivatePolicy = async (policyId) => {
    try {
      const res = await confirmationPolicyService.deactivateConfirmationPolicy(policyId);
      if (res.success) {
        toast.success("Policy deactivated");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Failed to deactivate policy");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to deactivate policy");
    }
  };

  const handleReactivatePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) return;
    try {
      const res = await confirmationPolicyService.updateConfirmationPolicy(policyId, {
        ...mergePayloadForApi(normalizePolicyRow(policy)),
        status: "active",
      });
      if (res.success) {
        toast.success("Policy reactivated");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Failed to reactivate policy");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to reactivate policy");
    }
  };

  const handleDeletePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    const policyName = policy?.policyName || policy?.name || "";
    await startPolicyDeleteFlow({
      policyId,
      policyName,
      getDeleteImpact: confirmationPolicyService.getConfirmationPolicyDeleteImpact,
      setDeleteModal: setDeletePolicyModal,
      onError: (message) => toast.error(message),
    });
  };

  const handleConfirmDeletePolicy = async () => {
    if (!deletePolicyModal?.policyId) return;
    await confirmPolicyDelete({
      policyId: deletePolicyModal.policyId,
      deletePolicy: confirmationPolicyService.deleteConfirmationPolicy,
      setDeleting: setDeletePolicyLoading,
      setDeleteModal: setDeletePolicyModal,
      onSuccess: () => {
        toast.success("Policy deleted");
        onPolicyChange?.();
      },
      onError: (message) => toast.error(message),
    });
  };

  const handleCopyPolicy = async (policy) => {
    const p = normalizePolicyRow(policy);
    const now = new Date().toISOString();
    const nm = `${p.policyName || p.name || "Policy"} (Copy)`;
    const eff = p.effectiveDate || new Date().toISOString().slice(0, 10);
    const copied = {
      ...createDefaultPolicy(),
      ...p,
      policyName: nm,
      name: nm,
      effectiveDate: eff,
      status: "active",
      createdAt: now,
      updatedAt: now,
      policyDocumentPushed: false,
      policyPushedAt: null,
      policyVersion: 1,
      policyVersionHistory: [
        {
          version: 1,
          effectiveDate: eff,
          savedAt: now,
          event: "created",
          policyName: nm,
        },
      ],
    };
    const snapBody = clonePolicySnapshot({
      ...copied,
      policySnapshots: undefined,
      policyVersion: 1,
    });
    copied.policySnapshots = [{ version: 1, effectiveDate: eff, savedAt: now, policy: snapBody }];
    try {
      const res = await confirmationPolicyService.createConfirmationPolicy(
        buildConfirmationPolicyApiPayload(copied, approvalRolesForPolicy)
      );
      if (res.success) {
        toast.success("Policy copied");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Copy failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Copy failed");
    }
  };

  const handlePushPolicyDocument = async (policy) => {
    const now = new Date().toISOString();
    try {
      const res = await confirmationPolicyService.updateConfirmationPolicy(policy.id, {
        ...mergePayloadForApi(normalizePolicyRow(policy)),
        policyDocumentPushed: true,
        policyPushedAt: now,
      });
      if (res.success) {
        setPolicyDocModal((m) =>
          m && m.id === policy.id ? { ...m, policyDocumentPushed: true, policyPushedAt: now } : m
        );
        setActionMenu(null);
        toast.success("Policy document published to assigned employees.");
        onPolicyChange?.();
      } else {
        toast.error(res.message || "Failed to publish policy document");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to publish policy document");
    }
  };

  const openPolicyDocumentPreview = (policy) => {
    const latest = policies.find((p) => p.id === policy.id) || policy;
    setPolicyDocModal(latest);
    setActionMenu(null);
  };

  const printPolicyFullDocument = (policy) => {
    const html = buildConfirmationPolicyDocumentHtml(policy, policyDocumentOptions);
    printConfirmationPolicyDocumentHtml(html);
  };

  const printPolicySnapshotDocument = (snapshotPolicy) => {
    const html = buildConfirmationPolicyDocumentHtml(snapshotPolicy, policyDocumentOptions);
    printConfirmationPolicyDocumentHtml(html);
  };

  const openActionMenuFromEvent = (e, policy) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 4;
    setActionMenu((prev) => (prev?.policy?.id === policy.id ? null : { policy, top, left }));
  };

  const addWorkflowStep = (key) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] || []),
        { role: defaultPolicyRoleValue || WORKFLOW_ROLES[0]?.value || "manager", slaDays: 3, notifyOnly: false },
      ],
    }));
  };
  const removeWorkflowStep = (key, index) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));
  };
  const updateWorkflowStep = (key, index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((step, i) => (i === index ? { ...step, [field]: value } : step)),
    }));
  };
  const addChecklistItem = () => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: [...(prev.checklistMappings || []), normalizeChecklistItem({})],
    }));
  };
  const removeChecklistItem = (index) => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: (prev.checklistMappings || []).filter((_, i) => i !== index),
    }));
  };
  const updateChecklistItem = (index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: (prev.checklistMappings || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const toggleApprovalRole = (roleValue) => {
    setApprovalRolesForPolicy((prev) =>
      prev.includes(roleValue)
        ? prev.filter((r) => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  const POLICY_SIGNATURE_OMIT = new Set([
    "id",
    "createdAt",
    "updatedAt",
    "policySnapshots",
    "policyVersionHistory",
    "policyDocumentPushed",
    "policyPushedAt",
    "policyVersion",
    "effectiveDate",
    "status",
  ]);

  const policyContentSignature = (p) => {
    if (!p) return "";
    const o = {};
    Object.keys(p)
      .sort()
      .forEach((k) => {
        if (POLICY_SIGNATURE_OMIT.has(k)) return;
        let v = p[k];
        if (k === "employmentTypes") v = Array.isArray(v) ? [...v].map(String).sort() : [];
        o[k] = v;
      });
    return JSON.stringify(o);
  };

  const parseIndividualIds = (policy) => {
    const s = policy.individualEmployeeIds;
    if (Array.isArray(s)) return s.map(String).filter(Boolean);
    return String(s || "")
      .split(/[\s,]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const normalizeEmploymentTypesArr = (policy) => {
    const a = policy.employmentTypes;
    if (!Array.isArray(a) || a.length === 0) return null;
    return [...new Set(a.map(String))].sort();
  };

  const scopesApplicabilityOverlap = (a, b) => {
    if (String(a.id) === String(b.id)) return false;

    const aIds = parseIndividualIds(a);
    const bIds = parseIndividualIds(b);
    const aAll = !!a.allEmployees;
    const bAll = !!b.allEmployees;
    const aHasOrg = ORG_APPLICABILITY_KEYS.some((k) => a[k]);
    const bHasOrg = ORG_APPLICABILITY_KEYS.some((k) => b[k]);

    if (!aAll && !bAll && !aHasOrg && !bHasOrg && aIds.length && bIds.length) {
      const setB = new Set(bIds);
      return aIds.some((id) => setB.has(id));
    }

    if (aAll || bAll) {
      const aEm = normalizeEmploymentTypesArr(a);
      const bEm = normalizeEmploymentTypesArr(b);
      if (aEm && bEm) {
        const inter = aEm.filter((t) => bEm.includes(t));
        if (inter.length === 0) return false;
      }
      return true;
    }

    for (const k of ORG_APPLICABILITY_KEYS) {
      const va = a[k] ? String(a[k]) : "";
      const vb = b[k] ? String(b[k]) : "";
      if (va && vb && va !== vb) return false;
    }

    const aEm = normalizeEmploymentTypesArr(a);
    const bEm = normalizeEmploymentTypesArr(b);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length === 0) return false;
    }

    if (aIds.length && bIds.length) {
      const setB = new Set(bIds);
      return aIds.some((id) => setB.has(id));
    }
    if ((aIds.length && (bHasOrg || bAll)) || (bIds.length && (aHasOrg || aAll))) return true;
    if (aIds.length || bIds.length) return true;

    return true;
  };

  const findConflictingPolicies = (candidate, list, excludeId) =>
    list.filter((p) => {
      if (p.status === "inactive") return false;
      if (excludeId && String(p.id) === String(excludeId)) return false;
      return scopesApplicabilityOverlap(candidate, p);
    });

  const masterName = (list, id) => {
    if (!id) return "";
    return (list || []).find((x) => String(x.id) === String(id))?.name || "";
  };

  const summarizeOverlapReason = (candidate, other) => {
    const bits = [];
    if (candidate.allEmployees || other.allEmployees) {
      bits.push(
        'One policy is set to "All employees" (or the whole organization). That scope overlaps with any other rule that still applies to those people — for example a department-only policy.'
      );
    }
    for (const k of ORG_APPLICABILITY_KEYS) {
      const va = candidate[k] ? String(candidate[k]) : "";
      const vb = other[k] ? String(other[k]) : "";
      if (va && vb && va === vb) {
        const label =
          k === "branch"
            ? "Branch"
            : k === "department"
            ? "Department"
            : k === "designation"
            ? "Designation"
            : k === "grade"
            ? "Grade"
            : k === "category"
            ? "Category"
            : k === "subDepartment"
            ? "Sub-department"
            : "Location";
        const list =
          k === "branch"
            ? branchesList
            : k === "department"
            ? departmentsList
            : k === "designation"
            ? designationsList
            : k === "grade"
            ? gradesList
            : k === "category"
            ? categoriesList
            : k === "subDepartment"
            ? subDepartmentsList
            : null;
        const nm = list ? masterName(list, va) : va;
        bits.push(`Same ${label}: "${nm || va}".`);
      }
    }
    const aEm = normalizeEmploymentTypesArr(candidate);
    const bEm = normalizeEmploymentTypesArr(other);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length) {
        bits.push(
          `Overlapping employment type(s): ${inter
            .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
            .join(", ")}.`
        );
      }
    } else if ((aEm || bEm) && (candidate.allEmployees || other.allEmployees)) {
      bits.push("Employment-type filters on one policy may still overlap with a broad 'all employees' policy.");
    }
    if (!bits.length) {
      bits.push(
        "These applicability rules may assign the same employees to more than one active confirmation policy. Saving will be final for this record."
      );
    }
    return bits.join(" ");
  };

  const toggleEmploymentType = (value) => {
    setNewPolicy((prev) => {
      const cur = Array.isArray(prev.employmentTypes) ? prev.employmentTypes : [];
      const has = cur.includes(value);
      return {
        ...prev,
        employmentTypes: has ? cur.filter((v) => v !== value) : [...cur, value],
      };
    });
  };

  /** Same row order and labels as PaceHRM EmployeeConfirmation getConfirmationPolicySummaryRows (HR policy print / preview). */
  const getPolicySummaryRows = (p) => {
    const indIds = Array.isArray(p.individualEmployeeIds)
      ? p.individualEmployeeIds.join(", ")
      : p.individualEmployeeIds || "";
    const appLabel = (list, value) => {
      if (!value) return "All";
      const found = (list || []).find((x) => String(x.id) === String(value));
      return found?.name || (value ? "—" : "All");
    };
    const wfSteps = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
    const extSteps = Array.isArray(p.extensionWorkflowSteps) ? p.extensionWorkflowSteps : [];
    const termSteps = Array.isArray(p.terminationWorkflowSteps) ? p.terminationWorkflowSteps : [];
    const cancelSteps = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
    const checklist = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];

    const rows = [];
    rows.push({
      label: "Policy name",
      value: typeof p.policyName === "string" ? p.policyName.trim() || p.policyName : p.policyName || "—",
    });
    rows.push({ label: "Effective date", value: p.effectiveDate || "—" });
    rows.push({
      label: "Policy document version",
      value: p.policyVersion != null ? String(p.policyVersion) : "1",
    });
    rows.push({
      label: "Auto-assign this policy on joining",
      value: p.autoAssignOnJoin ? "Yes" : "No",
    });
    rows.push({
      label: "Initial probation period",
      value:
        p.initialProbationPeriod === "custom"
          ? `Custom (${p.customProbationDays ?? 90} days)`
          : PROBATION_PERIOD_OPTIONS.find((o) => o.value === p.initialProbationPeriod)?.label ||
            p.initialProbationPeriod ||
            "—",
    });
    rows.push({
      label: "Start date logic",
      value: START_DATE_LOGIC_OPTIONS.find((o) => o.value === p.startDateLogic)?.label || p.startDateLogic || "—",
    });
    rows.push({
      label: "Minimum service days for confirmation",
      value: p.minServiceDaysForConfirmation ?? "—",
    });
    rows.push({
      label: "Auto-confirmation",
      value: p.autoConfirmation === "yes" ? "Yes" : "No",
    });
    rows.push({
      label: "Reminder trigger days (before completion)",
      value:
        Array.isArray(p.reminderTriggerDays) && p.reminderTriggerDays.length
          ? `${p.reminderTriggerDays.join(", ")} days`
          : "—",
    });
    rows.push({
      label: "Performance review mandatory before confirmation",
      value: p.performanceReviewMandatoryBeforeConfirmation === "yes" ? "Yes" : "No",
    });
    rows.push({
      label: "Extension allowed",
      value: p.extensionAllowed === "yes" ? "Yes" : "No",
    });
    rows.push({
      label: "Maximum number of extensions",
      value: p.extensionAllowed === "yes" ? p.maxExtensions ?? "—" : "—",
    });
    rows.push({
      label: "Allowed extension duration",
      value:
        p.extensionAllowed === "yes"
          ? p.extensionDurationType === "fixed"
            ? `Fixed (${p.extensionDurationDays ?? 90} days)`
            : "Custom per request"
          : "—",
    });
    rows.push({
      label: "Reason mandatory (extension)",
      value: p.extensionAllowed === "yes" ? (p.extensionReasonMandatory === "yes" ? "Yes" : "No") : "—",
    });
    rows.push({
      label: "Performance review mandatory before extension",
      value: p.extensionAllowed === "yes" ? (p.performanceReviewMandatoryBeforeExtension === "yes" ? "Yes" : "No") : "—",
    });
    rows.push({
      label: "Final extension auto-trigger termination",
      value: p.extensionAllowed === "yes" ? (p.finalExtensionAutoTriggerTermination === "yes" ? "Yes" : "No") : "—",
    });
    rows.push({
      label: "Termination allowed during probation",
      value: p.terminationDuringProbationAllowed === "yes" ? "Yes" : "No",
    });
    rows.push({
      label: "Notice period (days) (termination)",
      value: p.terminationNoticePeriodDays ?? "—",
    });
    rows.push({
      label: "Multi-level approval required (termination)",
      value: p.terminationMultiLevelApproval === "yes" ? "Yes" : "No",
    });
    rows.push({
      label: "Experience letter allowed",
      value: p.experienceLetterAllowed === "yes" ? "Yes" : "No",
    });
    rows.push({
      label: "Full & Final settlement trigger",
      value: p.fullAndFinalSettlementTrigger === "auto" ? "Auto" : "Manual",
    });
    rows.push({ label: "Probation duration (days)", value: p.probationDays ?? "—" });
    rows.push({
      label: "Approval method",
      value:
        p.approvalMethod === "workflow"
          ? "Through workflow"
          : p.approvalMethod === "on_instance"
          ? "On instance"
          : "Auto approved",
    });
    if (p.approvalMethod === "workflow") {
      rows.push({ label: "Reminder days before completion", value: p.reminderDays ?? "—" });
    }
    if (p.approvalMethod === "workflow") {
      rows.push({ label: "Limit probation extensions", value: p.limitProbationExtensions ?? "—" });
    }
    rows.push({ label: "Auto confirmation letter", value: p.autoLetter === "yes" ? "Yes" : "No" });
    if (p.autoLetter === "yes") {
      rows.push({
        label: "Letter template",
        value: LETTER_FORMAT_OPTIONS.find((o) => o.value === p.letterFormat)?.label || p.letterFormat || "—",
      });
    }
    rows.push({
      label: "Confirmation letter template",
      value: LETTER_FORMAT_OPTIONS.find((o) => o.value === p.letterConfirmation)?.label || p.letterConfirmation || "—",
    });
    rows.push({
      label: "Probation extension letter template",
      value: LETTER_FORMAT_OPTIONS.find((o) => o.value === p.letterExtension)?.label || p.letterExtension || "—",
    });
    rows.push({
      label: "Probation termination letter template",
      value: LETTER_FORMAT_OPTIONS.find((o) => o.value === p.letterTermination)?.label || p.letterTermination || "—",
    });
    rows.push({ label: "Parallel approval option", value: p.workflowParallelApproval ? "Yes" : "No" });
    rows.push({ label: "Escalation after (days)", value: p.workflowEscalationAfterDays ?? "—" });
    rows.push({
      label: "Auto-approval if no action",
      value: p.workflowAutoApprovalIfNoAction ? "Yes" : "No",
    });
    rows.push({
      label: "Rejection with reason",
      value: p.workflowRejectionWithReason ? "Yes" : "No",
    });
    rows.push({
      label: "Re-initiation allowed",
      value: p.workflowReinitiationAllowed ? "Yes" : "No",
    });
    if (wfSteps.length) {
      wfSteps.forEach((s, i) =>
        rows.push({
          label: `Step ${i + 1} (Confirmation)`,
          value: `${WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role}${
            s.notifyOnly ? " (Notify only)" : ` — SLA ${s.slaDays ?? 3} days`
          }`,
        })
      );
    } else {
      rows.push({ label: "Workflow steps (Confirmation)", value: "—" });
    }
    if (extSteps.length) {
      extSteps.forEach((s, i) =>
        rows.push({
          label: `Step ${i + 1} (Extension)`,
          value: `${WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role}${
            s.notifyOnly ? " (Notify only)" : ` — SLA ${s.slaDays ?? 3} days`
          }`,
        })
      );
    }
    if (termSteps.length) {
      termSteps.forEach((s, i) =>
        rows.push({
          label: `Step ${i + 1} (Termination)`,
          value: `${WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role}${
            s.notifyOnly ? " (Notify only)" : ` — SLA ${s.slaDays ?? 3} days`
          }`,
        })
      );
    }
    if (cancelSteps.length) {
      cancelSteps.forEach((s, i) =>
        rows.push({
          label: `Step ${i + 1} (Cancellation)`,
          value: `${WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role}${
            s.notifyOnly ? " (Notify only)" : ` — SLA ${s.slaDays ?? 3} days`
          }`,
        })
      );
    }
    if (checklist.length) {
      checklist.forEach((item, i) =>
        rows.push({
          label: `Checklist item ${i + 1}`,
          value: `Trigger: ${item.triggerEvent || "—"} | Dept: ${item.department || "—"} | ${item.name || "—"} | ${
            item.mandatory ? "Mandatory" : "Optional"
          } | Due: ${item.dueDateLogic || "—"}${item.dueDateDays ? ` + ${item.dueDateDays} days` : ""}`,
        })
      );
    } else {
      rows.push({ label: "Checklist mapping", value: "None configured" });
    }
    rows.push({
      label: "All employees (applicability)",
      value: p.allEmployees ? "Yes" : "No",
    });
    rows.push({
      label: "Employment types (applicability)",
      value:
        Array.isArray(p.employmentTypes) && p.employmentTypes.length
          ? p.employmentTypes
              .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
              .join(", ")
          : "All types",
    });
    rows.push({ label: "Branch (applicability)", value: appLabel(branchesList, p.branch) });
    rows.push({ label: "Department (applicability)", value: appLabel(departmentsList, p.department) });
    rows.push({ label: "Designation (applicability)", value: appLabel(designationsList, p.designation) });
    rows.push({ label: "Grade (applicability)", value: appLabel(gradesList, p.grade) });
    rows.push({ label: "Category (applicability)", value: appLabel(categoriesList, p.category) });
    rows.push({
      label: "Sub-department / Division (applicability)",
      value: appLabel(subDepartmentsList, p.subDepartment),
    });
    rows.push({
      label: "Location (applicability)",
      value: LOCATION_OPTIONS.find((o) => o.value === (p.location || ""))?.label || (p.location ? "—" : "All"),
    });
    rows.push({
      label: "Individual employees (override)",
      value: (typeof indIds === "string" && indIds.trim()) || "—",
    });
    return rows;
  };

  const getApprovalLevelsCount = (policy) => {
    if (policy.approvalMethod === "workflow" && Array.isArray(policy.workflowSteps)) {
      return (policy.workflowSteps || []).length;
    }
    return "—";
  };

  const printPolicyDocument = (policy) => {
    printSinglePolicyAsDocument(
      "Confirmation Policy",
      policy.policyName,
      policy.effectiveDate,
      getPolicySummaryRows,
      policy
    );
  };

  const getDynamicPolicyGuideLines = (policy) => {
    const p = policy || createDefaultPolicy();
    const confirmSteps = Array.isArray(p.workflowSteps) ? p.workflowSteps.length : 0;
    const extSteps = Array.isArray(p.extensionWorkflowSteps)
      ? p.extensionWorkflowSteps.length
      : 0;
    const termSteps = Array.isArray(p.terminationWorkflowSteps)
      ? p.terminationWorkflowSteps.length
      : 0;
    const checklistCount = Array.isArray(p.checklistMappings)
      ? p.checklistMappings.length
      : 0;

    return [
      `This policy uses "${p.approvalMethod === "workflow" ? "Through workflow" : p.approvalMethod === "on_instance" ? "On instance" : "Auto approved"}" approval mode.`,
      `Probation starts as "${p.initialProbationPeriod}" with minimum ${p.minServiceDaysForConfirmation || 0} service days.`,
      `Workflow steps configured: Confirmation ${confirmSteps}, Extension ${extSteps}, Termination ${termSteps}.`,
      `Checklist mappings configured: ${checklistCount}.`,
      `Applicability scope: ${p.allEmployees ? "All employees" : "Filtered by selected organization fields"}.`,
    ];
  };

  const escapeXml = (s) => {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  /**
   * Single continuous process: spine (request → reviews → one decision), then four outcomes in one band
   * (no separate “Confirmation / Extension / Termination” sections).
   */
  const buildUnifiedConfirmationProcessSvg = (policy) => {
    const p = policy || {};
    const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
    const ext = Array.isArray(p.extensionWorkflowSteps) ? p.extensionWorkflowSteps : [];
    const term = Array.isArray(p.terminationWorkflowSteps) ? p.terminationWorkflowSteps : [];
    const chkCount = Array.isArray(p.checklistMappings) ? p.checklistMappings.length : 0;
    const autoLetter = p.autoLetter === "yes";
    const reinit = p.workflowReinitiationAllowed !== false;
    const extAllowed = p.extensionAllowed === "yes";
    const termAllowed = p.terminationDuringProbationAllowed === "yes";
    const fnfLabel = p.fullAndFinalSettlementTrigger === "auto" ? "Auto" : "Manual";

    const cx = 460;
    let y = 32;
    const parts = [];
    const pushLine = (y0, y1) => {
      parts.push(
        `<line x1="${cx}" y1="${y0}" x2="${cx}" y2="${y1}" stroke="#64748b" stroke-width="2" marker-end="url(#uflow-arr)"/>`
      );
    };

    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="15" font-weight="700" fill="#0f172a">Confirmation process (single flow)</text>`
    );
    y += 22;
    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="10" fill="#64748b">One process: request → reviews → one decision → approve, extend, reject, or terminate.</text>`
    );
    y += 30;

    parts.push(`<circle cx="${cx}" cy="${y}" r="14" stroke="#64748b" fill="#fff" stroke-width="2"/>`);
    parts.push(`<text x="${cx}" y="${y + 4}" text-anchor="middle" font-size="9" fill="#334155">START</text>`);
    y += 22;
    pushLine(y - 8, y);
    const reqH = 44;
    parts.push(
      `<rect x="${cx - 120}" y="${y}" width="240" height="${reqH}" rx="8" fill="#fff" stroke="#334155" stroke-width="1.5"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 20}" text-anchor="middle" font-size="11" font-weight="700" fill="#0f172a">Confirmation request</text>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 36}" text-anchor="middle" font-size="9" fill="#475569">Submitted · pending review</text>`
    );
    y += reqH + 14;

    if (wf.length) {
      wf.forEach((s, i) => {
        pushLine(y - 14, y);
        const role = WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role;
        const sub = s.notifyOnly ? "Notify only" : `Approve / SLA ${s.slaDays || 3}d`;
        parts.push(
          `<rect x="${cx - 115}" y="${y}" width="230" height="40" rx="6" fill="#f8fafc" stroke="#64748b" stroke-width="1.2"/>`
        );
        parts.push(
          `<text x="${cx}" y="${y + 16}" text-anchor="middle" font-size="10" font-weight="600" fill="#0f172a">Review ${i + 1}: ${escapeXml(
            role
          )}</text>`
        );
        parts.push(
          `<text x="${cx}" y="${y + 32}" text-anchor="middle" font-size="9" fill="#475569">${escapeXml(sub)}</text>`
        );
        y += 54;
      });
    } else {
      pushLine(y - 14, y);
      parts.push(
        `<rect x="${cx - 115}" y="${y}" width="230" height="38" rx="6" fill="#fefce8" stroke="#ca8a04" stroke-width="1.2"/>`
      );
      parts.push(
        `<text x="${cx}" y="${y + 24}" text-anchor="middle" font-size="9" fill="#713f12">No steps listed — routing follows approval method / defaults</text>`
      );
      y += 52;
    }

    pushLine(y - 14, y);
    const d = 26;
    const decisionTop = y;
    parts.push(
      `<polygon points="${cx},${y} ${cx + d},${y + d} ${cx},${y + 2 * d} ${cx - d},${y + d}" fill="#fff" stroke="#0f172a" stroke-width="2"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + d + 4}" text-anchor="middle" font-size="8" font-weight="700" fill="#0f172a">Decision</text>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 2 * d - 4}" text-anchor="middle" font-size="7" fill="#64748b">Approve · Extend · Reject · Terminate</text>`
    );
    const decisionBottom = y + 2 * d;
    y = decisionBottom + 20;
    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="9" font-weight="600" fill="#475569">Then choose an outcome (same process, four paths)</text>`
    );
    y += 22;

    const approveLines = [];
    approveLines.push(
      wf.length ? `Complete confirmation reviews (${wf.length} step(s))` : "Complete review per approval method"
    );
    approveLines.push(
      autoLetter
        ? "Issue confirmation letter · run checklist"
        : `Run checklist (${chkCount} item(s)) · letter optional`
    );
    approveLines.push("END: Employee confirmed");

    const extendLines = [];
    if (!extAllowed) {
      extendLines.push("Extension not allowed by policy");
    } else if (ext.length) {
      ext.forEach((s, i) => {
        const role = WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role;
        extendLines.push(
          `Extension approval ${i + 1}: ${role}${s.notifyOnly ? " (notify)" : ` · SLA ${s.slaDays || 3}d`}`
        );
      });
    } else {
      extendLines.push("Add extension workflow steps in policy");
    }
    extendLines.push("Probation end date extended");
    extendLines.push("Next: new review cycle → returns to confirmation decision");
    extendLines.push("END: Continues until confirmed or other outcome");

    const rejectLines = [];
    rejectLines.push("Request not approved");
    rejectLines.push(p.workflowRejectionWithReason !== false ? "Reason captured (if enabled)" : "Reason optional");
    rejectLines.push(reinit ? "May submit a new confirmation request" : "Case closed (no resubmit)");
    rejectLines.push("END: Rejected / closed");

    const termLines = [];
    if (!termAllowed) {
      termLines.push("Termination during probation disabled");
    } else if (term.length) {
      term.forEach((s, i) => {
        const role = WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role;
        termLines.push(
          `Termination approval ${i + 1}: ${role}${s.notifyOnly ? " (notify)" : ` · SLA ${s.slaDays || 3}d`}`
        );
      });
    } else {
      termLines.push("Add termination workflow steps in policy");
    }
    termLines.push(`Notice · separation · F&F settlement: ${fnfLabel}`);
    termLines.push("END: Employment separated");

    const outcomeDefs = [
      { title: "Approve", color: "#16a34a", lines: approveLines },
      { title: "Extend", color: "#0284c7", lines: extendLines },
      { title: "Reject", color: "#dc2626", lines: rejectLines },
      { title: "Terminate", color: "#9333ea", lines: termLines },
    ];

    const boxW = 198;
    const gap = 12;
    const lineH = 12;
    const padTop = 22;
    const heights = outcomeDefs.map((o) => padTop + o.lines.length * lineH + 10);
    const boxH = Math.max(...heights);
    const totalW = 4 * boxW + 3 * gap;
    const startX = cx - totalW / 2;
    const rowTop = y;

    const centers = [0, 1, 2, 3].map((i) => startX + i * (boxW + gap) + boxW / 2);
    const colors = ["#16a34a", "#0284c7", "#dc2626", "#9333ea"];
    centers.forEach((bx, i) => {
      parts.push(
        `<path d="M ${cx} ${decisionBottom} L ${bx} ${rowTop - 2}" fill="none" stroke="${colors[i]}" stroke-width="1.4" marker-end="url(#uflow-arr)"/>`
      );
    });

    outcomeDefs.forEach((o, i) => {
      const x = startX + i * (boxW + gap);
      parts.push(
        `<rect x="${x}" y="${rowTop}" width="${boxW}" height="${boxH}" rx="8" fill="#fafafa" stroke="${o.color}" stroke-width="2"/>`
      );
      parts.push(
        `<text x="${x + 10}" y="${rowTop + 16}" font-size="10" font-weight="700" fill="${o.color}">${escapeXml(
          o.title
        )}</text>`
      );
      o.lines.forEach((ln, j) => {
        parts.push(
          `<text x="${x + 10}" y="${rowTop + padTop + j * lineH}" font-size="8.5" fill="#334155">${escapeXml(ln)}</text>`
        );
      });
    });

    let H = rowTop + boxH + 28;
    if (extAllowed) {
      const extCx = centers[1];
      const loopY = rowTop + boxH + 8;
      parts.push(
        `<path d="M ${extCx} ${loopY} Q ${cx} ${loopY + 48} ${cx} ${decisionTop}" fill="none" stroke="#94a3b8" stroke-width="1" stroke-dasharray="4 3"/>`
      );
      H = rowTop + boxH + 52;
      parts.push(
        `<text x="${cx}" y="${H - 10}" text-anchor="middle" font-size="8" fill="#64748b">Dashed: extension path loops back toward the same decision</text>`
      );
    }
    const W = 920;
    return `
      <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="max-width:100%;height:auto">
        <defs>
          <marker id="uflow-arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 8 4, 0 8" fill="#64748b"/>
          </marker>
        </defs>
        ${parts.join("")}
      </svg>
    `;
  };

  const printCombinedFlowchartPdf = (policy) => {
    const html = `
      <html>
      <head>
        <title>Confirmation process — ${policy?.policyName || "Policy"}</title>
        <style>
          body { font-family: "Segoe UI", Inter, Arial, sans-serif; margin: 20px; color: #0f172a; }
          h2 { margin: 0 0 12px 0; font-size: 16px; }
          .hint { font-size: 12px; color: #475569; margin-bottom: 14px; }
          .flow { border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; page-break-inside: avoid; }
          @media print { .flow { break-inside: avoid; } body { margin: 12px; } }
        </style>
      </head>
      <body>
        <h2>Confirmation process — ${policy?.policyName || "Policy"}</h2>
        <div class="hint">Single end-to-end flow (print or Save as PDF from the dialog).</div>
        <div class="flow">${buildUnifiedConfirmationProcessSvg(policy || {})}</div>
      </body>
      </html>
    `;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const printAllPolicies = () => {
    printAllPoliciesAsDocument("Confirmation Policy", policies, (p) => getPolicySummaryRows(p));
  };

  const getApplicabilityBadges = (policy) => {
    const badges = [];
    if (policy.allEmployees) badges.push("All employees");
    if (!policy.allEmployees) {
      const addBadge = (list, key, labelPrefix) => {
        if (!policy[key]) return;
        const item = (list || []).find((x) => String(x.id) === String(policy[key]));
        badges.push(`${labelPrefix}: ${item?.name || policy[key]}`);
      };
      addBadge(branchesList, "branch", "Branch");
      addBadge(departmentsList, "department", "Dept");
      addBadge(designationsList, "designation", "Designation");
      addBadge(gradesList, "grade", "Grade");
      addBadge(categoriesList, "category", "Category");
      addBadge(subDepartmentsList, "subDepartment", "Sub Dept");
    }
    if (Array.isArray(policy.employmentTypes) && policy.employmentTypes.length) {
      const labels = policy.employmentTypes.map(
        (v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v
      );
      badges.push(`Employment: ${labels.join(", ")}`);
    }
    return badges.length ? badges : ["No scope"];
  };

  const visiblePolicies = policies
    .filter((p) => (showInactivePolicies ? p.status === "inactive" : p.status === "active"))
    .filter((p) =>
      String(p.policyName || p.name || "")
        .toLowerCase()
        .includes(String(policySearch || "").toLowerCase().trim())
    );
  const listedPolicies = visiblePolicies.slice(0, 10);
  const selectedApprovalRoleLabels = useMemo(
    () =>
      APPROVAL_ROLES.filter((r) => approvalRolesForPolicy.includes(r.value)).map(
        (r) => r.label
      ),
    [approvalRolesForPolicy]
  );

  const validatePolicyFormStep = (step) => {
    if (step === 1) {
      return validateBasicsStep(newPolicy);
    }
    if (step === 2) {
      if (
        newPolicy.initialProbationPeriod === "custom" &&
        (!newPolicy.customProbationDays || Number(newPolicy.customProbationDays) <= 0)
      ) {
        return "Probation period must be greater than 0.";
      }
      if (newPolicy.extensionAllowed === "yes") {
        if (!newPolicy.maxExtensions || Number(newPolicy.maxExtensions) < 1) {
          return "Max extension count must be at least 1.";
        }
        if (
          newPolicy.extensionDurationType === "fixed" &&
          (!newPolicy.extensionDurationDays || Number(newPolicy.extensionDurationDays) < 1)
        ) {
          return "Extension duration must be at least 1.";
        }
      }
    }
    if (step === 3) {
      return validateWorkflowStep(newPolicy);
    }
    if (step === 4) {
      const letterErr = validateLettersStep(newPolicy, { letterField: "letterConfirmation" });
      if (letterErr) return letterErr;
      if (newPolicy.autoLetter === "yes" && !String(newPolicy.letterFormat || "").trim()) {
        return "Choose a letter template when auto letter is enabled.";
      }
    }
    if (step === 5) {
      return validateChecklistStep(newPolicy);
    }
    if (step === 6) {
      return validateApplicabilityStep(newPolicy);
    }
    return null;
  };

  const { footer: wizardFooter, handleStepClick } = createPolicyWizardNav({
    policyFormStep,
    steps: CONFIRMATION_POLICY_STEPS,
    validateStep: validatePolicyFormStep,
    setPolicyFormStep,
    setPolicySaveError,
    onCancel: handleCancelPolicy,
    onPreviewSave: requestSavePolicyForm,
    onFlowchartPreview: () => setFlowchartPolicy(newPolicy),
    darkMode,
  });

  return (
    <div className="flex h-full min-h-0 flex-col bg-[hsl(var(--ds-surface))] p-3 text-[hsl(var(--ds-fg))] sm:p-4">
      <PolicyListToolbar
        title="Confirmation Policies"
        newLabel="+ New Confirmation Policy"
        onNew={openCreateForm}
        onPrint={printAllPolicies}
        searchValue={policySearch}
        onSearchChange={(e) => setPolicySearch(e.target.value)}
        showInactive={showInactivePolicies}
        onToggleInactive={() => setShowInactivePolicies((v) => !v)}
        rolesSlot={
          <div className="relative" ref={approvalSettingsRef}>
            <DsButton
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setApprovalSettingsOpen((v) => !v)}
              title="Roles that can approve policies"
            >
              Roles
            </DsButton>
            {approvalSettingsOpen && (
              <div className="absolute right-0 z-20 mt-2 w-64 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2 shadow-[var(--ds-shadow-card)]">
                <div className="mb-2 border-b border-[hsl(var(--ds-border))] px-2 py-1 text-xs font-semibold uppercase text-[hsl(var(--ds-muted-fg))]">
                  Roles that can approve policy
                </div>
                {APPROVAL_ROLES.map((role) => (
                  <label key={role.value} className="flex items-center gap-2 px-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={approvalRolesForPolicy.includes(role.value)}
                      onChange={() => toggleApprovalRole(role.value)}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        }
      />

      <div className="ds-table-wrap min-h-0 flex-1">
        {loadingPolicies ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading confirmation policies...</div>
        ) : (
        <div className="max-h-[62vh] overflow-y-auto">
          <table className="ds-table w-full text-left">
            <PolicyListTableHeader ruleColumnLabel="Probation" />
            <tbody className="divide-y divide-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
              {listedPolicies.map((policy, idx) => {
                const badges = getApplicabilityBadges(policy);
                return (
                  <tr key={policy.id} className="hover:bg-[hsl(var(--ds-muted))]/50 transition-colors">
                    <td className="px-4 py-3 text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-[hsl(var(--ds-fg))]">
                      {policy.policyName || policy.name}
                    </td>
                    <td className="px-4 py-3 text-center text-[hsl(var(--ds-fg))] tabular-nums">
                      v{policy.policyVersion ?? 1}
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">{policy.probationDays || 90}d</td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">{policy.reminderDays || policy.reminderTriggerDay || 7}d</td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">
                      {policy.approvalMethod === "workflow"
                        ? "Workflow"
                        : policy.approvalMethod === "on_instance"
                        ? "On instance"
                        : "Auto approved"}
                    </td>
                    <td className="px-4 py-3 text-center text-[hsl(var(--ds-fg))]">
                      {getApprovalLevelsCount(policy)}
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--ds-fg))]">
                      {policy.autoLetter === "yes" ? "Yes" : "No"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <PolicyAppliedMastersEye
                        policyName={policy.policyName || policy.name}
                        badges={badges}
                        policyDocumentPushed={!!policy.policyDocumentPushed}
                        darkMode={darkMode}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className={policyListStatusClass(policy.status === "active")}>
                        {policy.status || "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        aria-label="Row actions"
                        className={policyListRowActionClass}
                        onClick={(e) => openActionMenuFromEvent(e, policy)}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {listedPolicies.length === 0 && (
        <p className={`text-sm py-6 text-center ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          {policySearch.trim()
            ? "No policies match your search."
            : 'No policies. Click "New Confirmation Policy" to create one.'}
        </p>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]">
          <div
            className={`w-[min(92vw,780px)] h-[min(90vh,720px)] rounded-2xl shadow-2xl border flex flex-col overflow-hidden ${
              darkMode
                ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
            }`}
          >
            <div
              className={`px-5 py-4 flex items-center justify-between flex-shrink-0 border-b ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <div>
                <h3 className={`text-[15px] font-semibold ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                  {isEditing ? "Edit confirmation policy" : "New confirmation policy"}
                </h3>
                <p className={`text-[11px] mt-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  Complete all six steps to configure this confirmation policy.
                </p>
              </div>
              <div className="flex items-center gap-2 relative" ref={policyHelpRef}>
                <button
                  type="button"
                  onClick={() => setPolicyHelpOpen((v) => !v)}
                  className={darkMode ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"}
                  title="Field guide"
                >
                  <Info size={18} />
                </button>
                <button
                  onClick={handleCancelPolicy}
                  className={darkMode ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"}
                >
                  <X size={18} />
                </button>
                {policyHelpOpen && (
                  <div className="absolute right-0 top-8 w-[420px] max-w-[80vw] p-3 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-lg rounded-xl z-20 text-xs text-[hsl(var(--ds-fg))] space-y-1">
                    {getDynamicPolicyGuideLines(newPolicy).map((line, idx) => (
                      <p key={`guide-${idx}`}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
            <PolicyFormWizardShell
              darkMode={darkMode}
              steps={CONFIRMATION_POLICY_STEPS}
              currentStep={policyFormStep}
              onStepClick={handleStepClick}
              policySaveError={policySaveError}
              footer={
                <>
                  {wizardFooter}
                  {policyFormStep === CONFIRMATION_POLICY_STEPS.length && savingPolicy && (
                    <span className="text-xs text-[hsl(var(--ds-muted-fg))] w-full sm:w-auto text-right">Saving...</span>
                  )}
                </>
              }
            >
              {policyFormStep === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className={`block text-xs font-medium mb-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>Policy Name</label>
                  <input
                    type="text"
                    value={newPolicy.policyName || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, policyName: e.target.value }))}
                    className={`w-full py-1.5 px-2.5 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                    placeholder="e.g. Default Confirmation Policy"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-0.5 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>Effective Date</label>
                  <input
                    type="date"
                    value={newPolicy.effectiveDate || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, effectiveDate: e.target.value }))}
                    className={`w-full py-1.5 px-2.5 border rounded-lg text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                  />
                  <p className={`text-[10px] mt-1 leading-snug ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                    Policy document version increases automatically when you save with a new effective date that is after the
                    previous one and is a future date (today excluded). Current document version: v
                    {newPolicy.policyVersion ?? 1}.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.autoAssignOnJoin}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, autoAssignOnJoin: e.target.checked }))}
                      className="mr-2"
                    />
                    Auto-assign this policy on joining
                  </label>
                </div>
              </div>
              )}

              {policyFormStep === 2 && (
              <>
              <div
                className={`p-3 border rounded-xl shadow-sm ${
                  darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.5)]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                }`}
              >
                <h4 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Probation & Confirmation Rules</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Initial Probation Period</label>
                    <select
                      value={newPolicy.initialProbationPeriod}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, initialProbationPeriod: e.target.value }))}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                    >
                      {PROBATION_PERIOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  {newPolicy.initialProbationPeriod === "custom" && (
                    <div>
                      <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Custom Probation (days)</label>
                      <input
                        type="number"
                        min="1"
                        value={newPolicy.customProbationDays}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, customProbationDays: Number(e.target.value) || 90 }))}
                        className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                      />
                    </div>
                  )}
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Start Date Logic</label>
                    <select
                      value={newPolicy.startDateLogic}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, startDateLogic: e.target.value }))}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                    >
                      {START_DATE_LOGIC_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Minimum Service Days</label>
                    <input
                      type="number"
                      min="1"
                      value={newPolicy.minServiceDaysForConfirmation}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, minServiceDaysForConfirmation: Number(e.target.value) || 90 }))}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Confirmation Type</label>
                    <select
                      value={newPolicy.confirmationType}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, confirmationType: e.target.value }))}
                      className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}
                    >
                      <option value="automatic">Automatic</option>
                      <option value="manual">Manual</option>
                      <option value="performance">Performance-Based</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Reminder Trigger Days (before completion)</label>
                    <div className="flex gap-4">
                      {REMINDER_DAYS_OPTIONS.map((d) => (
                        <label key={d} className="flex items-center text-sm">
                          <input
                            type="radio"
                            name="reminderTriggerDay"
                            checked={Number(newPolicy.reminderTriggerDay) === d}
                            onChange={() => setNewPolicy((p) => ({ ...p, reminderTriggerDay: d }))}
                            className="mr-2"
                          />
                          {d} days
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Performance review mandatory before confirmation</label>
                    <select value={newPolicy.performanceReviewMandatoryBeforeConfirmation} onChange={(e) => setNewPolicy((p) => ({ ...p, performanceReviewMandatoryBeforeConfirmation: e.target.value }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={`mt-4 p-3 border rounded-none ${darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"}`}>
                <h4 className={`text-sm font-semibold mb-2 ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>Extension & Termination Rules</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Extension Allowed</label>
                    <select value={newPolicy.extensionAllowed} onChange={(e) => setNewPolicy((p) => ({ ...p, extensionAllowed: e.target.value }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {newPolicy.extensionAllowed === "yes" && (
                    <>
                      <div>
                        <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Max Extensions</label>
                        <input type="number" min="1" value={newPolicy.maxExtensions} onChange={(e) => setNewPolicy((p) => ({ ...p, maxExtensions: Number(e.target.value) || 1 }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`} />
                      </div>
                      <div>
                        <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Extension Duration</label>
                        <select value={newPolicy.extensionDurationType} onChange={(e) => setNewPolicy((p) => ({ ...p, extensionDurationType: e.target.value }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                          {EXTENSION_DURATION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      {newPolicy.extensionDurationType === "fixed" && (
                        <div>
                          <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Extension Duration Days</label>
                          <input type="number" min="1" value={newPolicy.extensionDurationDays} onChange={(e) => setNewPolicy((p) => ({ ...p, extensionDurationDays: Number(e.target.value) || 90 }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`} />
                        </div>
                      )}
                      <div>
                        <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Extension reason mandatory</label>
                        <select value={newPolicy.extensionReasonMandatory} onChange={(e) => setNewPolicy((p) => ({ ...p, extensionReasonMandatory: e.target.value }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>Termination during probation allowed</label>
                    <select value={newPolicy.terminationDuringProbationAllowed} onChange={(e) => setNewPolicy((p) => ({ ...p, terminationDuringProbationAllowed: e.target.value }))} className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))] text-white" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"}`}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              </div>
              </>
              )}

              {policyFormStep === 3 && (
              <PolicyWorkflowStepSection
                darkMode={darkMode}
                policy={newPolicy}
                setPolicy={setNewPolicy}
                workflowStepsTab={workflowStepsTab}
                setWorkflowStepsTab={setWorkflowStepsTab}
                policyRoleOptions={policyRoleOptions}
                addWorkflowStep={addWorkflowStep}
                removeWorkflowStep={removeWorkflowStep}
                updateWorkflowStep={updateWorkflowStep}
                tabs={CONFIRMATION_WORKFLOW_TABS}
                approvalMethodOptions={CONFIRMATION_APPROVAL_METHODS}
                autoApprovedMessage="Confirmation requests under this policy are approved automatically once submitted — no reviewer chain is required."
                reminderDaysLabel="Reminder days before confirmation due"
              />
              )}

              {policyFormStep === 4 && (
              <PolicyLettersStepSection darkMode={darkMode}>
                <PolicyLettersField darkMode={darkMode} label="Auto confirmation letter">
                  <PolicyLettersYesNo
                    darkMode={darkMode}
                    value={newPolicy.autoLetter}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, autoLetter: e.target.value }))}
                  />
                </PolicyLettersField>
                <PolicyLettersField darkMode={darkMode} label="Confirmation letter template">
                  <PolicyLettersSelect
                    darkMode={darkMode}
                    value={newPolicy.letterConfirmation || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, letterConfirmation: e.target.value }))}
                    options={LETTER_FORMAT_OPTIONS}
                  />
                </PolicyLettersField>
                <PolicyLettersField darkMode={darkMode} label="Letter template">
                  <PolicyLettersSelect
                    darkMode={darkMode}
                    value={newPolicy.letterFormat || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, letterFormat: e.target.value }))}
                    options={LETTER_FORMAT_OPTIONS}
                  />
                </PolicyLettersField>
                <PolicyLettersField darkMode={darkMode} label="Extension letter template">
                  <PolicyLettersSelect
                    darkMode={darkMode}
                    value={newPolicy.letterExtension || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, letterExtension: e.target.value }))}
                    options={LETTER_FORMAT_OPTIONS}
                  />
                </PolicyLettersField>
                <PolicyLettersField darkMode={darkMode} label="Termination letter template">
                  <PolicyLettersSelect
                    darkMode={darkMode}
                    value={newPolicy.letterTermination || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, letterTermination: e.target.value }))}
                    options={LETTER_FORMAT_OPTIONS}
                  />
                </PolicyLettersField>
                <PolicyLettersField darkMode={darkMode} label="Full & final settlement trigger">
                  <PolicyLettersSelect
                    darkMode={darkMode}
                    value={newPolicy.fullAndFinalSettlementTrigger || ""}
                    onChange={(e) =>
                      setNewPolicy((p) => ({ ...p, fullAndFinalSettlementTrigger: e.target.value }))
                    }
                    options={FNF_TRIGGER_OPTIONS}
                  />
                </PolicyLettersField>
              </PolicyLettersStepSection>
              )}

              {policyFormStep === 5 && (
              <PolicyChecklistStepSection
                darkMode={darkMode}
                policy={newPolicy}
                checklistMappings={newPolicy.checklistMappings || []}
                description="Checklist items triggered on Confirmation / Extension / Termination approval."
                emptyHint="Add checklist items (e.g. Salary revision, Insurance enrollment, ID card, Asset allocation, Access upgrade, Clearance)."
                triggerEvents={CHECKLIST_TRIGGER_EVENTS}
                departments={CHECKLIST_DEPARTMENTS}
                dueDateLogicOptions={DUE_DATE_LOGIC_OPTIONS}
                policyRoleOptions={policyRoleOptions}
                addChecklistItem={addChecklistItem}
                removeChecklistItem={removeChecklistItem}
                updateChecklistItem={updateChecklistItem}
              />
              )}

              {policyFormStep === 6 && (
              <PolicyApplicabilityStepSection
                darkMode={darkMode}
                policy={newPolicy}
                setPolicy={setNewPolicy}
                branchesList={branchesList}
                departmentsList={departmentsList}
                designationsList={designationsList}
                gradesList={gradesList}
                categoriesList={categoriesList}
                subDepartmentsList={subDepartmentsList}
                locationOptions={LOCATION_OPTIONS}
                employmentTypeOptions={EMPLOYMENT_TYPE_OPTIONS}
                onToggleEmploymentType={toggleEmploymentType}
              />
              )}
            </PolicyFormWizardShell>
            </div>
          </div>
        </div>
      )}
      {deletePolicyModal && (
        <PolicyDeleteImpactModal
          open
          mode={deletePolicyModal.mode}
          impact={deletePolicyModal.impact}
          policyName={deletePolicyModal.policyName}
          deleting={deletePolicyLoading}
          onClose={() => setDeletePolicyModal(null)}
          onConfirmDelete={handleConfirmDeletePolicy}
        />
      )}
      {saveGateModal?.type === "effective_date" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
          <div className="w-[min(100%,480px)] bg-[hsl(var(--ds-surface))] border border-amber-300 shadow-xl rounded-none p-6">
            <h3 className="text-lg font-semibold text-amber-900">Effective date must be updated</h3>
            <p className="text-sm text-[hsl(var(--ds-fg))] mt-3 leading-relaxed">
              You changed policy content (rules, applicability, workflows, etc.) but the <strong>effective date</strong> is
              still the same as the saved version. For audit and employee communication, you must set a new effective
              date—typically a <strong>future</strong> date—whenever the policy wording or configuration changes.
            </p>
            <p className="text-sm text-[hsl(var(--ds-muted-fg))] mt-2">Update the effective date in the form, then save again.</p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium rounded-none bg-amber-700 text-white hover:bg-amber-800"
                onClick={() => setSaveGateModal(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {saveGateModal?.type === "overlap" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
          <div className="w-[min(100%,560px)] max-h-[85vh] bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-xl rounded-none flex flex-col">
            <div className="px-5 py-4 border-b border-[hsl(var(--ds-border))] bg-amber-50">
              <h3 className="text-base font-semibold text-amber-950">Applicability overlap</h3>
              <p className="text-sm text-[hsl(var(--ds-fg))] mt-2 leading-relaxed">
                Another active policy already covers some of the same employees under this combination of scope (for
                example, &quot;All employees&quot; overlaps a department-only policy, or two policies share the same
                department and employment types). Assigning more than one confirmation policy to the same people can
                cause conflicting rules.
              </p>
              <p className="text-sm font-medium text-[hsl(var(--ds-fg))] mt-2">Saving this policy will be final for this record.</p>
            </div>
            <div className="px-5 py-3 overflow-y-auto flex-1">
              <ul className="space-y-3 text-sm text-[hsl(var(--ds-fg))]">
                {saveGateModal.items.map((item) => (
                  <li key={item.id} className="border border-[hsl(var(--ds-border))] rounded-none p-3 bg-[hsl(var(--ds-muted))]">
                    <p className="font-semibold text-[hsl(var(--ds-fg))]">{item.name}</p>
                    <p className="text-[hsl(var(--ds-muted-fg))] mt-1 text-xs leading-relaxed">{item.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 py-4 border-t border-[hsl(var(--ds-border))] flex flex-wrap gap-2 justify-end bg-[hsl(var(--ds-surface))]">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => setSaveGateModal(null)}
              >
                Back to edit
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-none inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                onClick={continueAfterOverlapAck}
              >
                I understand — continue to preview &amp; save
              </button>
            </div>
          </div>
        </div>
      )}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-[hsl(var(--ds-surface))] shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-[hsl(var(--ds-fg))]">Policy Preview</h3>
              <button onClick={() => setShowSummaryModal(false)} className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <div className="text-sm text-[hsl(var(--ds-fg))] space-y-2">
                {getPolicySummaryRows(newPolicy).map((row, idx) => (
                  <div key={`sum-${idx}`} className="grid grid-cols-3 gap-3 border-b pb-2">
                    <p className="font-medium col-span-1">{row.label}</p>
                    <p className="col-span-2">{String(row.value ?? "—")}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-sm text-[hsl(var(--ds-muted-fg))]">
                <strong>Roles that can approve:</strong> {selectedApprovalRoleLabels.join(", ") || "—"}
              </div>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowSummaryModal(false)}
                className="px-4 py-2 text-sm bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
              >
                Back
              </button>
              <button
                onClick={handleSavePolicy}
                disabled={savingPolicy}
                className="px-4 py-2 text-sm inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95 disabled:opacity-60"
              >
                {savingPolicy ? "Saving..." : "Save Policy"}
              </button>
            </div>
          </div>
        </div>
      )}
      {previewPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-[hsl(var(--ds-surface))] shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-[hsl(var(--ds-fg))]">Saved Policy Preview</h3>
              <button onClick={() => setPreviewPolicy(null)} className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto text-sm text-[hsl(var(--ds-fg))] space-y-2">
              {getPolicySummaryRows(previewPolicy).map((row, idx) => (
                <div key={`prev-${idx}`} className="grid grid-cols-3 gap-3 border-b pb-2">
                  <p className="font-medium col-span-1">{row.label}</p>
                  <p className="col-span-2">{String(row.value ?? "—")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {flowchartPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[min(96vw,1120px)] max-h-[90vh] bg-[hsl(var(--ds-surface))] rounded-xl shadow-2xl border border-[hsl(var(--ds-border))] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-[hsl(var(--ds-fg))] text-sm">
                  Preview flowchart — {flowchartPolicy.policyName || "Policy"}
                </h3>
                <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
                  Single process: request, decision, and all four outcomes with next steps. Use Print to save as PDF.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => printCombinedFlowchartPdf(flowchartPolicy)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[hsl(var(--ds-surface))] text-white hover:bg-[hsl(var(--ds-surface))]"
                >
                  <Printer size={14} />
                  Print / Save as PDF
                </button>
                <button
                  type="button"
                  onClick={() => setFlowchartPolicy(null)}
                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto bg-[hsl(var(--ds-surface))]">
              <div
                className="w-full overflow-x-auto rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/50 p-3"
                dangerouslySetInnerHTML={{ __html: buildUnifiedConfirmationProcessSvg(flowchartPolicy) }}
              />
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-3">
                Colored connectors match each outcome box. The dashed curve shows how an extension leads back toward the
                same decision on a later review.
              </p>
            </div>
          </div>
        </div>
      )}
      {actionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[10018]" aria-hidden onClick={() => setActionMenu(null)} />
            <div
              className="fixed z-[10019] w-56 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 text-left text-sm shadow-lg dark:border-[hsl(var(--ds-border))] dark:bg-[#1D1D1D]"
              style={{ top: actionMenu.top, left: actionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  openEditForm(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  setPreviewPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Preview (configuration)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => openPolicyDocumentPreview(actionMenu.policy)}
              >
                Policy document (HR)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  setFlowchartPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Flowchart
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  printPolicyDocument(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Print (configuration summary)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-muted))] dark:text-[hsl(var(--ds-accent-fg))] dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  if (
                    window.confirm(
                      "Publish this policy document to employees covered by this policy (assigned scope)?"
                    )
                  ) {
                    handlePushPolicyDocument(actionMenu.policy);
                  }
                }}
              >
                Push policy document
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] dark:text-gray-200 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  handleCopyPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Copy
              </button>
              {actionMenu.policy.status === "active" ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-[hsl(var(--ds-surface))]"
                  onClick={() => {
                    handleDeactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-[hsl(var(--ds-surface))]"
                  onClick={() => {
                    handleReactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Reactivate
                </button>
              )}
              <button
                type="button"
                className="w-full border-t border-[hsl(var(--ds-border))] px-3 py-2 text-left text-sm text-[hsl(var(--ds-primary))] hover:bg-rose-50 dark:border-[hsl(var(--ds-border))] dark:text-rose-400 dark:hover:bg-[hsl(var(--ds-surface))]"
                onClick={() => {
                  handleDeletePolicy(actionMenu.policy.id);
                  setActionMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}
      {policyDocModal &&
        (() => {
          const docPolicy = policies.find((p) => p.id === policyDocModal.id) || policyDocModal;
          const docHtml = buildConfirmationPolicyDocumentHtml(docPolicy, policyDocumentOptions);
          return (
            <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/50">
              <div className="w-[min(96vw,880px)] max-h-[92vh] bg-[hsl(var(--ds-surface))] flex flex-col border border-[hsl(var(--ds-border))] rounded-none shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-[hsl(var(--ds-border))] flex items-center justify-between gap-3 bg-[hsl(var(--ds-muted))]">
                  <div>
                    <h3 className="font-semibold text-[hsl(var(--ds-fg))] text-sm">Policy document</h3>
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
                      {docCompanyName}
                      {docCompanyAddress ? ` · ${docCompanyAddress.split("\n")[0]}` : ""}
                    </p>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1 font-medium">{docPolicy.policyName || "Policy"}</p>
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-0.5">
                      Document v{docPolicy.policyVersion ?? 1} · Effective {docPolicy.effectiveDate || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyDocModal(null);
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                    className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden bg-[hsl(var(--ds-muted))] p-2">
                  <iframe title="Policy document" className="w-full h-[min(70vh,640px)] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" srcDoc={docHtml} />
                </div>
                <div className="px-4 py-3 border-t border-[hsl(var(--ds-border))] flex flex-wrap gap-2 justify-between items-center bg-[hsl(var(--ds-surface))]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setPolicyHistoryModalId(docPolicy.id);
                        setHistoryPreviewEntry(null);
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      View history
                    </button>
                    {docPolicy.policyDocumentPushed && docPolicy.policyPushedAt && (
                      <span className="text-xs text-emerald-800">
                        Published: {new Date(docPolicy.policyPushedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center justify-end">
                    <button
                      type="button"
                      onClick={() => printPolicyFullDocument(docPolicy)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      <Printer size={14} />
                      Print document
                    </button>
                    {!docPolicy.policyDocumentPushed ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Publish this policy document to employees covered by this policy (assigned scope)?"
                            )
                          ) {
                            handlePushPolicyDocument(docPolicy);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-none inline-flex items-center justify-center rounded-md bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-sm font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-45 hover:opacity-95"
                      >
                        Push to assigned employees
                      </button>
                    ) : (
                      <span className="text-xs text-[hsl(var(--ds-muted-fg))]">Already pushed — use Print to share a copy.</span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPolicyDocModal(null);
                        setPolicyHistoryModalId(null);
                        setHistoryPreviewEntry(null);
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      {policyHistoryModalId &&
        (() => {
          const histPolicy = policies.find((p) => p.id === policyHistoryModalId);
          if (!histPolicy) return null;
          const rows = getPolicySnapshotsForDisplay(histPolicy);
          const previewHtml = historyPreviewEntry
            ? buildConfirmationPolicyDocumentHtml(historyPreviewEntry.policy, policyDocumentOptions)
            : "";
          return (
            <div className="fixed inset-0 z-[10003] flex items-center justify-center p-4 bg-black/55">
              <div className="w-[min(96vw,900px)] max-h-[92vh] bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-none shadow-xl flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b border-[hsl(var(--ds-border))] flex justify-between items-start gap-3 bg-[hsl(var(--ds-muted))]">
                  <div>
                    <h3 className="font-semibold text-[hsl(var(--ds-fg))] text-sm">Policy version history</h3>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">{histPolicy.policyName || "Policy"}</p>
                    <p className="text-[11px] text-[hsl(var(--ds-muted-fg))] mt-2 max-w-[52rem] leading-relaxed">
                      The document version number increases when you save with a new <strong>effective date</strong> that is
                      later than the previous effective date and is strictly <strong>after today’s date</strong> (future
                      effective date). Ordinary edits without that change keep the same version. Each row below is a stored
                      snapshot of the policy wording for that version — preview or print any version for records or employee
                      communication.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                    className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] p-1 flex-shrink-0"
                    aria-label="Close history"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  <table className="w-full text-left text-xs border border-[hsl(var(--ds-border))] border-collapse">
                    <thead>
                      <tr className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))]">
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))]">Version</th>
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))]">Effective date</th>
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))]">Recorded at</th>
                        <th className="px-3 py-2 font-semibold text-[hsl(var(--ds-fg))] text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={`${row.version}-${row.savedAt}`} className="border-b border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted)/0.35)]">
                          <td className="px-3 py-2 font-medium text-[hsl(var(--ds-fg))]">{row.version}</td>
                          <td className="px-3 py-2 text-[hsl(var(--ds-fg))]">{row.effectiveDate || "—"}</td>
                          <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">
                            {row.savedAt ? new Date(row.savedAt).toLocaleString() : "—"}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              type="button"
                              className="text-[hsl(var(--ds-secondary))] hover:underline mr-3 font-medium"
                              onClick={() => setHistoryPreviewEntry(row)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="text-[hsl(var(--ds-fg))] hover:underline font-medium"
                              onClick={() => printPolicySnapshotDocument(row.policy)}
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {historyPreviewEntry && (
                    <div className="mt-4 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-2">
                      <p className="text-xs font-semibold text-[hsl(var(--ds-fg))] mb-2">
                        Preview — version {historyPreviewEntry.version} (effective {historyPreviewEntry.effectiveDate || "—"})
                      </p>
                      <iframe
                        title="Policy version preview"
                        className="w-full h-[min(50vh,480px)] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                        srcDoc={previewHtml}
                      />
                    </div>
                  )}
                </div>
                <div className="px-4 py-3 border-t border-[hsl(var(--ds-border))] flex justify-end bg-[hsl(var(--ds-surface))]">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                    onClick={() => {
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default ConfirmationPolicyComponent;
